import { isEmpty } from '@sgme/fp';
import { AddonManagementForm } from 'components/AddonManagement/AddonManagementForm';
import { AddonManagementFormInteractionToolbar } from 'components/AddonManagement/AddonManagementFormInteractionToolbar';
import { AddonManagementFormTableHeader } from 'components/AddonManagement/AddonManagementFormTableHeader';
import { AddonManagementLoadingSkeleton } from 'components/AddonManagement/LoadingSkeleton/AddonManagementLoadingSkeleton';
import { EmptyState } from 'components/shared/EmptyState/EmptyState';
import { useSelector } from 'react-redux';
import type { ProductGraph } from 'state/product-description/model';
import {
  getAddonManagementFormFieldIds,
  getAddonManagementSelectedFormsLength,
  getFormIdsInAddonManagement,
  isColumnHiddenForAllForms,
} from 'state/selectors';
import type { AppState } from 'state/state.model';
import {
  getAddonManagementWorkspaceLoading,
  isAddonManagementWorkspaceLoading,
} from 'state/workspaces/addonManagement/addonManagement.selectors';

import { GridContainer } from './GridUtils';

export interface AddonManagementFormTableProps {
  productDescription: ProductGraph;
}

export function AddonManagementFormTable({ productDescription }: AddonManagementFormTableProps) {
  const formIds = useSelector(getFormIdsInAddonManagement);
  const formFieldIds = useSelector(getAddonManagementFormFieldIds);
  const selectedFormsLength = useSelector(getAddonManagementSelectedFormsLength);

  const isUpfrontFeeColumnHidden = useSelector((state: AppState) => isColumnHiddenForAllForms(state, 'UpfrontFee'));
  const isOfferPriceColumnHidden = useSelector((state: AppState) => isColumnHiddenForAllForms(state, 'OfferPrice'));
  const isFixedAmountInPercentColumnHidden = useSelector((state: AppState) =>
    isColumnHiddenForAllForms(state, 'FixedAmountInPercent'),
  );
  const isTradingPriceColumnHidden = useSelector((state: AppState) => isColumnHiddenForAllForms(state, 'TradingPrice'));

  const isLoading = useSelector(isAddonManagementWorkspaceLoading);
  const error = useSelector(getAddonManagementWorkspaceLoading);

  if (isLoading) {
    return (
      <div className="overflow-none flex-fill">
        <AddonManagementLoadingSkeleton />
      </div>
    );
  }

  if (isEmpty(formIds) || error) {
    return (
      <div className="d-flex justify-content-center h-100">
        <EmptyState prefix="addonManagement" e2e="addon-management" />
      </div>
    );
  }

  const gridColumnSize =
    Number(productDescription[0]?.children?.length) -
    Number(isUpfrontFeeColumnHidden) -
    Number(isOfferPriceColumnHidden) -
    Number(isFixedAmountInPercentColumnHidden) -
    Number(isTradingPriceColumnHidden) -
    1;

  return (
    <>
      <div className="h-100 overflow-y-auto bg-lvl4 tabular-figures border-top" data-e2e="addon-management-grid-table">
        <GridContainer $columnSize={gridColumnSize} className="bg-lvl1" role="grid">
          <AddonManagementFormTableHeader
            productDescription={productDescription}
            isUpfrontFeeColumnHidden={isUpfrontFeeColumnHidden}
            isOfferPriceColumnHidden={isOfferPriceColumnHidden}
            isFixedAmountInPercentColumnHidden={isFixedAmountInPercentColumnHidden}
            isTradingPriceColumnHidden={isTradingPriceColumnHidden}
          />
          {formFieldIds.map(formId => (
            <AddonManagementForm
              key={formId}
              formId={formId}
              productDescription={productDescription}
              isUpfrontFeeColumnHidden={isUpfrontFeeColumnHidden}
              isOfferPriceColumnHidden={isOfferPriceColumnHidden}
              isFixedAmountInPercentColumnHidden={isFixedAmountInPercentColumnHidden}
              isTradingPriceColumnHidden={isTradingPriceColumnHidden}
            />
          ))}
        </GridContainer>
      </div>
      {selectedFormsLength > 0 && (
        <AddonManagementFormInteractionToolbar classNames="is-sticky-bottom bg-info h-auto" />
      )}
    </>
  );
}
